import { useTranslation } from 'react-i18next';
import { PlanLayout } from "@/components/plans/PlanLayout";
import { DollarSign, TrendingUp, Scale, PiggyBank, FileText } from "lucide-react";
import { ContactForm } from "@/components/ContactForm";
import { motion } from "framer-motion";

const DDSPage = () => {
  const { t } = useTranslation();

  const benefits = [
    { Icon: Scale, title: t('plans_detail.dds.benefit_0_title'), desc: t('plans_detail.dds.benefit_0_desc') },
    { Icon: TrendingUp, title: t('plans_detail.dds.benefit_1_title'), desc: t('plans_detail.dds.benefit_1_desc') },
    { Icon: PiggyBank, title: t('plans_detail.dds.benefit_2_title'), desc: t('plans_detail.dds.benefit_2_desc') },
    { Icon: DollarSign, title: t('plans_detail.dds.benefit_3_title'), desc: t('plans_detail.dds.benefit_3_desc') },
  ];

  const features = [
    { label: t('plans_detail.dds.feature_0_label'), desc: t('plans_detail.dds.feature_0_desc') },
    { label: t('plans_detail.dds.feature_1_label'), desc: t('plans_detail.dds.feature_1_desc') },
    { label: t('plans_detail.dds.feature_2_label'), desc: t('plans_detail.dds.feature_2_desc') },
    { label: t('plans_detail.dds.feature_3_label'), desc: t('plans_detail.dds.feature_3_desc') },
    { label: t('plans_detail.dds.feature_4_label'), desc: t('plans_detail.dds.feature_4_desc') },
  ];

  const eligibilityItems = [
    t('plans_detail.dds.eligibility_0'),
    t('plans_detail.dds.eligibility_1'),
    t('plans_detail.dds.eligibility_2'),
    t('plans_detail.dds.eligibility_3'),
    t('plans_detail.dds.eligibility_4'),
  ];

  const processSteps = [
    { label: t('plans_detail.dds.process_0_label'), desc: t('plans_detail.dds.process_0_desc') },
    { label: t('plans_detail.dds.process_1_label'), desc: t('plans_detail.dds.process_1_desc') },
    { label: t('plans_detail.dds.process_2_label'), desc: t('plans_detail.dds.process_2_desc') },
    { label: t('plans_detail.dds.process_3_label'), desc: t('plans_detail.dds.process_3_desc') },
    { label: t('plans_detail.dds.process_4_label'), desc: t('plans_detail.dds.process_4_desc') },
  ];

  const keyPoints = [
    { title: t('plans_detail.dds.key_0_title'), desc: t('plans_detail.dds.key_0_desc') },
    { title: t('plans_detail.dds.key_1_title'), desc: t('plans_detail.dds.key_1_desc') },
    { title: t('plans_detail.dds.key_2_title'), desc: t('plans_detail.dds.key_2_desc') },
    { title: t('plans_detail.dds.key_3_title'), desc: t('plans_detail.dds.key_3_desc') },
  ];

  const supportItems = [
    t('plans_detail.dds.support_0'),
    t('plans_detail.dds.support_1'),
    t('plans_detail.dds.support_2'),
    t('plans_detail.dds.support_3'),
    t('plans_detail.dds.support_4'),
  ];

  return (
    <PlanLayout
      title={t('plans_detail.dds.title')}
      subtitle={t('plans_detail.dds.subtitle')}
      imageUrl="https://images.unsplash.com/photo-1565372195458-9de0b320ef04"
    >
      <div className="space-y-8">
        <motion.section
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <h2 className="text-2xl font-bold mb-4 text-white">{t('plans_detail.common.overview')}</h2>
          <p className="text-white/70 leading-relaxed">{t('plans_detail.dds.overview_p1')}</p>
        </motion.section>

        <motion.section
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <h2 className="text-2xl font-bold mb-4 text-white">{t('plans_detail.dds.benefits_title')}</h2>
          <motion.div
            className="grid gap-4 md:grid-cols-2"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={{ hidden: {}, visible: { transition: { staggerChildren: 0.08 } } }}
          >
            {benefits.map(({ Icon, title, desc }) => (
              <motion.div
                key={title}
                variants={{ hidden: { opacity: 0, y: 16 }, visible: { opacity: 1, y: 0, transition: { duration: 0.4 } } }}
                whileHover={{ scale: 1.02 }}
                className="border border-white/10 bg-white/5 p-5 rounded-xl"
              >
                <div className="flex items-center mb-2 gap-2">
                  <Icon className="text-[#c9a84c]" size={20} />
                  <h3 className="font-bold text-white">{title}</h3>
                </div>
                <p className="text-white/65 text-sm leading-relaxed">{desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </motion.section>

        <motion.section
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <h2 className="text-2xl font-bold mb-4 text-white">{t('plans_detail.dds.features_title')}</h2>
          <div className="border border-white/10 bg-white/5 p-6 rounded-xl">
            <ul className="space-y-3 text-white/70">
              {features.map(({ label, desc }) => (
                <li key={label}><span className="font-bold text-white">{label}</span>：{desc}</li>
              ))}
            </ul>
          </div>
        </motion.section>

        <motion.section
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <h2 className="text-2xl font-bold mb-4 text-white">{t('plans_detail.dds.eligibility_title')}</h2>
          <p className="mb-4 text-white/70">{t('plans_detail.dds.eligibility_intro')}</p>
          <ul className="list-disc pl-5 space-y-2 text-white/70">
            {eligibilityItems.map((item, i) => (
              <li key={i}>{item}</li>
            ))}
          </ul>
        </motion.section>

        <motion.section
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <h2 className="text-2xl font-bold mb-4 text-white">{t('plans_detail.dds.process_title')}</h2>
          <div className="border border-white/10 bg-white/5 p-6 rounded-xl">
            <ol className="list-decimal pl-5 space-y-3 text-white/70">
              {processSteps.map(({ label, desc }) => (
                <li key={label}><span className="font-bold text-white">{label}</span>：{desc}</li>
              ))}
            </ol>
          </div>
        </motion.section>

        <motion.section
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <h2 className="text-2xl font-bold mb-4 text-white">{t('plans_detail.dds.key_points_title')}</h2>
          <div className="space-y-3">
            {keyPoints.map((item) => (
              <div key={item.title} className="flex items-start gap-3">
                <FileText className="text-[#c9a84c] mt-1 flex-shrink-0" size={18} />
                <p className="text-white/70 leading-relaxed"><span className="font-bold text-white">{item.title}</span>：{item.desc}</p>
              </div>
            ))}
          </div>
        </motion.section>

        <motion.section
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="border border-[#c9a84c]/30 bg-[#c9a84c]/10 p-6 rounded-xl"
        >
          <h2 className="text-2xl font-bold mb-4 text-white">{t('plans_detail.dds.our_support_title')}</h2>
          <p className="mb-4 text-white/70">{t('plans_detail.dds.our_support_intro')}</p>
          <ul className="list-disc pl-5 space-y-2 text-white/70 mb-4">
            {supportItems.map((item, i) => (
              <li key={i}>{item}</li>
            ))}
          </ul>
          <p className="mt-4 text-white/70">{t('plans_detail.dds.support_conclusion')}</p>
          <div className="mt-6">
            <h3 className="font-bold text-lg mb-3 text-white">{t('plans_detail.dds.contact_form_title')}</h3>
            <ContactForm
              buttonText={t('plans_detail.dds.contact_button')}
              className="border-[#c9a84c] text-[#c9a84c] hover:bg-[#c9a84c]/10"
            />
          </div>
        </motion.section>
      </div>
    </PlanLayout>
  );
};

export default DDSPage;
